import { useState } from "react";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../redux/store/store";
import { setTokens } from "../../redux/slice/authSlice";
import { loginAs, enterOrganization } from "../../redux/slice/layoutSlice";
import {
  useGetPublicPlansQuery,
  useSubmitEnterpriseInquiryMutation,
  useSignupMutation,
} from "../../redux/api";
import type { Plan } from "../../redux/api";
import type { XRole } from "../../config/module-types";
import { PLATFORM } from "../../config/platform";
import { Field } from "../../components/Field";
import { Modal } from "../../components/Modal";
import { PhoneInput } from "../../components/PhoneInput";
import { COUNTRIES, DEFAULT_COUNTRY_ISO, flagEmoji, getCountry } from "../../data/countries";
import {
  validateEmail,
  validatePhone,
  isValidEmail,
  isValidPhone,
} from "../../utils/validation";
import type { Region } from "../../utils/planPricing";
import { priceLabel, regionAmount } from "../../utils/planPricing";
import {
  IconBox,
  IconShield,
  IconChart,
  IconCheck,
  IconEye,
  IconEyeOff,
} from "../../components/icons";

interface LandingProps {
  /** Navigate to the sign-in screen. */
  onSignIn: () => void;
  /** Start signup for a chosen plan. */
  onBuy: (planId: string) => void;
  /** Open a public content page. */
  onShowPage: (page: "about" | "privacy" | "terms" | "contact") => void;
}

const FEATURES = [
  {
    icon: <IconBox />,
    title: "Inventory you can trust",
    text: "Track products, warehouses and stock across every location in real time.",
  },
  {
    icon: <IconChart />,
    title: "Insight at a glance",
    text: "Dashboards and reports that turn raw stock data into decisions.",
  },
  {
    icon: <IconShield />,
    title: "Built for teams",
    text: "Roles for owners, managers and employees — everyone sees just what they need.",
  },
];

// Fixed left-to-right display order on the landing page.
const PLAN_ORDER = ["PRO", "PRO_PLUS", "CUSTOM"];
const planRank = (code?: string | null) => {
  const index = PLAN_ORDER.indexOf((code ?? "").toUpperCase());
  return index === -1 ? PLAN_ORDER.length : index;
};


const planFeatures = (plan: Plan): string[] => {
  const limits = plan.limits ?? {};
  const cap = (n: number | null | undefined, label: string) =>
    n == null || n === 0
      ? `Unlimited ${label}`
      : `Up to ${n.toLocaleString()} ${label}`;
  return [
    cap(limits.users, "users"),
    cap(limits.products, "products"),
    cap(limits.storageGB, "GB storage"),
    plan.description?.trim() || "Email support",
  ];
};

// Public marketing landing page shown to unauthenticated visitors.
export const Landing = ({ onSignIn, onBuy, onShowPage }: LandingProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: plans = [], isLoading } = useGetPublicPlansQuery();

  const [submitEnterprise, { isLoading: submitting }] =
    useSubmitEnterpriseInquiryMutation();

  // "Start your 14-day trial" skips plan selection entirely — it signs the
  // visitor straight up (no status is sent, so the org lands on "trial" with
  // a 14-day trialEndDate, same as any self-serve signup) on the featured
  // plan's limits. Picking a specific plan still goes through the full
  // Signup screen via "Buy plan" on a plan card.
  const [signup, { isLoading: trialSubmitting }] = useSignupMutation();
  const [trialOpen, setTrialOpen] = useState(false);
  const [trialError, setTrialError] = useState<string | null>(null);
  const [trialFieldErrors, setTrialFieldErrors] = useState<
    Record<string, string>
  >({});
  const [showTrialPassword, setShowTrialPassword] = useState(false);
  const [trial, setTrial] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    organizationName: "",
  });

  const setTrialField = (key: keyof typeof trial, value: string) => {
    setTrial((prev) => ({ ...prev, [key]: value }));
    setTrialFieldErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const openTrial = () => {
    setTrial({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      organizationName: "",
    });
    setShowTrialPassword(false);
    setTrialError(null);
    setTrialFieldErrors({});
    setTrialOpen(true);
  };

  const closeTrial = () => setTrialOpen(false);

  const purchasablePlans = plans.filter(
    (p) => (p.code ?? "").toUpperCase() !== "CUSTOM",
  );
  const trialPlanId =
    purchasablePlans.find((p) => (p.code ?? "").toUpperCase() === "PRO_PLUS")
      ?.id ?? purchasablePlans[0]?.id;

  const handleTrialSubmit = async () => {
    setTrialError(null);

    const errors: Record<string, string> = {};
    if (!trial.firstName.trim()) errors.firstName = "First name is required.";
    if (!trial.lastName.trim()) errors.lastName = "Last name is required.";
    const emailError = validateEmail(trial.email);
    if (emailError) errors.email = emailError;
    const trialPhoneError = validatePhone(trial.phone, true);
    if (trialPhoneError) errors.phone = trialPhoneError;
    if (trial.password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
    }
    if (!trial.organizationName.trim()) {
      errors.organizationName = "Organization name is required.";
    }
    if (Object.keys(errors).length > 0) {
      setTrialFieldErrors(errors);
      return;
    }
    setTrialFieldErrors({});

    if (!trialPlanId) {
      setTrialError("Plans are still loading — please try again in a moment.");
      return;
    }

    try {
      const tokens = await signup({
        firstName: trial.firstName.trim(),
        lastName: trial.lastName.trim(),
        email: trial.email.trim(),
        phone: trial.phone.trim() || undefined,
        password: trial.password,
        organizationName: trial.organizationName.trim(),
        planId: trialPlanId,
      }).unwrap();

      dispatch(setTokens(tokens));
      dispatch(
        loginAs({
          name: tokens.name || `${trial.firstName} ${trial.lastName}`.trim(),
          email: tokens.email || trial.email,
          role: (tokens.role as XRole) || "owner",
          organizationId: tokens.organizationId ?? undefined,
        }),
      );
      if (tokens.organizationId) {
        dispatch(
          enterOrganization({
            organizationId: tokens.organizationId,
            organizationName: trial.organizationName.trim(),
            role: (tokens.role as XRole) || "owner",
          }),
        );
      }
      closeTrial();
    } catch (err) {
      setTrialError(
        (err as { message?: string })?.message ??
          "Could not create your account.",
      );
    }
  };
  const [contactOpen, setContactOpen] = useState(false);
  const [contactDone, setContactDone] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [contactFieldErrors, setContactFieldErrors] = useState<
    Record<string, string>
  >({});
  const [contact, setContact] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    country: DEFAULT_COUNTRY_ISO,
    message: "",
  });

  const setContactField = (key: keyof typeof contact, value: string) => {
    setContact((prev) => ({ ...prev, [key]: value }));
    setContactFieldErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const openContact = () => {
    setContact({
      name: "",
      email: "",
      company: "",
      phone: "",
      country: DEFAULT_COUNTRY_ISO,
      message: "",
    });
    setContactError(null);
    setContactFieldErrors({});
    setContactDone(false);
    setContactOpen(true);
  };

  const closeContact = () => setContactOpen(false);

  const handleContactSubmit = async () => {
    setContactError(null);

    const errors: Record<string, string> = {};
    if (!contact.name.trim()) errors.name = "Name is required.";
    const emailError = validateEmail(contact.email);
    if (emailError) errors.email = emailError;
    const phoneError = validatePhone(contact.phone, true);
    if (phoneError) errors.phone = phoneError;
    if (Object.keys(errors).length > 0) {
      setContactFieldErrors(errors);
      return;
    }
    setContactFieldErrors({});

    try {
      await submitEnterprise({
        name: contact.name.trim(),
        email: contact.email.trim(),
        company: contact.company.trim() || undefined,
        phone: contact.phone.trim() || undefined,
        country: getCountry(contact.country).name,
        message: contact.message.trim() || undefined,
      }).unwrap();
      setContactDone(true);
    } catch (err) {
      setContactError(
        (err as { message?: string })?.message ??
          "Could not send your message. Please try again.",
      );
    }
  };

  // Drives the "Send message" button's disabled state (separate from the
  // per-field errors below, which only appear after a submit attempt).
  const contactValid =
    contact.name.trim() !== "" &&
    isValidEmail(contact.email) &&
    contact.phone.trim() !== "" &&
    isValidPhone(contact.phone);

  // Fixed order: Trial, Hobby, Pro, Enterprise (by plan code, not price).
  const sortedPlans = [...plans].sort(
    (a, b) => planRank(a.code) - planRank(b.code),
  );

  const [region, setRegion] = useState<Region>("IN");

  return (
    <div className="landing">
      {/* Header */}
      <header className="landing__header">
        <div className="landing__brand">
          <img className="landing__logo" src={PLATFORM.logoSrc} alt="" aria-hidden="true" />
          <span className="landing__brand-name">{PLATFORM.platform_name}</span>
        </div>
        <nav className="landing__nav">
          <a href="#features">Features</a>
          <a href="#plans">Plans</a>
          <a href="#resellers">Resellers</a>
        </nav>
        <button type="button" className="btn btn--primary" onClick={onSignIn}>
          Sign in
        </button>
      </header>

      {/* Hero */}
      <section className="landing__hero">
        <h1 className="landing__title">
          Inventory management, <span className="landing__title-accent">simplified</span>.
        </h1>
        <p className="landing__subtitle">
          Run your products, warehouses, sales and team from one place — whether
          you're a single shop or a multi-location business.
        </p>
        <div className="landing__cta-row">
          <button
            type="button"
            className="btn btn--primary landing__cta"
            onClick={openTrial}
          >
            Start your 14-day trial
          </button>
          <button type="button" className="btn landing__cta" onClick={onSignIn}>
            Sign in
          </button>
        </div>
      </section>

      {/* Features */}
      <section className="landing__section" id="features">
        <h2 className="landing__section-title">Everything you need to stay in stock</h2>
        <div className="landing__features">
          {FEATURES.map((f) => (
            <div key={f.title} className="landing__feature">
              <span className="landing__feature-icon">{f.icon}</span>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Plans */}
      <section className="landing__section" id="plans">
        <h2 className="landing__section-title">Simple, transparent pricing</h2>
        <p className="landing__section-sub">Try Pro free for 14 days. Upgrade when you grow.</p>

        <div className="region-toggle" role="group" aria-label="Pricing region">
          <button
            type="button"
            className={`region-toggle__btn${region === "IN" ? " region-toggle__btn--active" : ""}`}
            onClick={() => setRegion("IN")}
          >
            🇮🇳 India (₹)
          </button>
          <button
            type="button"
            className={`region-toggle__btn${region === "INTL" ? " region-toggle__btn--active" : ""}`}
            onClick={() => setRegion("INTL")}
          >
            🌍 International ($)
          </button>
        </div>

        {isLoading ? (
          <p className="landing__section-sub">Loading plans…</p>
        ) : sortedPlans.length === 0 ? (
          <p className="landing__section-sub">
            Plans are being set up — please check back soon.
          </p>
        ) : (
          <div className="landing__plans">
            {sortedPlans.map((plan) => {
              const isCustom = (plan.code ?? "").toUpperCase() === "CUSTOM";
              const featured = (plan.code ?? "").toUpperCase() === "PRO_PLUS";
              return (
                <div
                  key={plan.id}
                  className={`plan-card${featured ? " plan-card--featured" : ""}`}
                >
                  {featured ? (
                    <span className="plan-card__badge">Most popular</span>
                  ) : null}
                  <h3 className="plan-card__name">{plan.name}</h3>
                  {/* <p className="plan-card__tagline">{plan.code}</p> */}
                  <div className="plan-card__price">
                    <span className="plan-card__amount">{priceLabel(plan, region)}</span>
                    {!isCustom && regionAmount(plan, region, "monthly") ? (
                      <span className="plan-card__period">/mo</span>
                    ) : null}
                  </div>
                  <ul className="plan-card__features">
                    {planFeatures(plan).map((feat) => (
                      <li key={feat}>
                        <span className="plan-card__check">
                          <IconCheck />
                        </span>
                        {feat}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className={`btn ${featured ? "btn--primary" : ""} plan-card__buy`}
                    onClick={() =>
                      isCustom ? openContact() : onBuy(plan.id)
                    }
                  >
                    {isCustom ? "Contact sales" : "Buy plan"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Reseller / partner */}
      <section className="landing__reseller" id="resellers">
        <div className="landing__reseller-body">
          <h2>Become a reseller partner</h2>
          <p>
            Resell {PLATFORM.platform_name} to your clients and earn recurring commission on
            every organization you bring on board. Track your referrals and
            payouts from your own broker portal.
          </p>
          <button type="button" className="btn btn--primary" onClick={onSignIn}>
            Join as a partner
          </button>
        </div>
        <div className="landing__reseller-stats">
          <div>
            <span className="landing__stat-value">20%</span>
            <span className="landing__stat-label">recurring commission</span>
          </div>
          <div>
            <span className="landing__stat-value">∞</span>
            <span className="landing__stat-label">clients you can bring</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing__footer">
        <div className="landing__footer-cols">
          <div className="landing__footer-col">
            <div className="landing__brand">
              <img className="landing__logo" src={PLATFORM.logoSrc} alt="" aria-hidden="true" />
              <span className="landing__brand-name">{PLATFORM.platform_name}</span>
            </div>
            <p className="landing__footer-note">
              Inventory & operations for modern businesses.
            </p>
          </div>
          <div className="landing__footer-col">
            <h4>Product</h4>
            <a href="#features">Features</a>
            <a href="#plans">Plans</a>
            <a href="#resellers">Resellers</a>
          </div>
          <div className="landing__footer-col">
            <h4>Company</h4>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onShowPage("about");
              }}
            >
              About
            </a>
            <a href="#">Careers</a>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onShowPage("contact");
              }}
            >
              Contact
            </a>
          </div>
          <div className="landing__footer-col">
            <h4>Legal</h4>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onShowPage("privacy");
              }}
            >
              Privacy
            </a>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onShowPage("terms");
              }}
            >
              Terms
            </a>
            <a href="#">Security</a>
          </div>
        </div>
        <div className="landing__footer-bottom">
          <span>© {"2026"} {PLATFORM.platform_name}. All rights reserved.</span>
          <button type="button" className="btn btn--ghost" onClick={onSignIn}>
            Sign in
          </button>
        </div>
      </footer>

      {/* Enterprise "Contact sales" popup */}
      <Modal
        open={contactOpen}
        title="Talk to our sales team"
        onClose={closeContact}
        footer={
          contactDone ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={closeContact}
            >
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn"
                onClick={closeContact}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn--primary"
                onClick={handleContactSubmit}
                disabled={submitting || !contactValid}
              >
                {submitting ? "Sending…" : "Send message"}
              </button>
            </>
          )
        }
      >
        {contactDone ? (
          <div className="profile-empty">
            Thanks! Our team will reach out about the Enterprise plan shortly.
          </div>
        ) : (
          <>
            {contactError ? (
              <div className="form-error">{contactError}</div>
            ) : null}
            <div className="form-grid">
              <Field label="Name *" error={contactFieldErrors.name}>
                <input
                  value={contact.name}
                  onChange={(e) => setContactField("name", e.target.value)}
                />
              </Field>
              <Field label="Work email *" error={contactFieldErrors.email}>
                <input
                  type="email"
                  value={contact.email}
                  onChange={(e) => setContactField("email", e.target.value)}
                  required
                />
              </Field>
              <label className="field">
                Company
                <input
                  value={contact.company}
                  onChange={(e) => setContactField("company", e.target.value)}
                />
              </label>
              <Field label="Phone *" error={contactFieldErrors.phone}>
                <PhoneInput
                  value={contact.phone}
                  onChange={(v) => setContactField("phone", v)}
                  required
                />
              </Field>
              <label className="field">
                Country
                <select
                  value={contact.country}
                  onChange={(e) => setContactField("country", e.target.value)}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.iso2} value={c.iso2}>
                      {flagEmoji(c.iso2)} {c.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field field--full">
                Message
                <textarea
                  value={contact.message}
                  onChange={(e) => setContactField("message", e.target.value)}
                  placeholder="Tell us about your team size and needs…"
                />
              </label>
            </div>
          </>
        )}
      </Modal>

      {/* "Start your 14-day trial" popup — no plan picker, just an account. */}
      <Modal
        open={trialOpen}
        title="Start your 14-day trial"
        onClose={closeTrial}
        footer={
          <>
            <button
              type="button"
              className="btn"
              onClick={closeTrial}
              disabled={trialSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleTrialSubmit}
              disabled={trialSubmitting}
            >
              {trialSubmitting ? "Creating account…" : "Create account"}
            </button>
          </>
        }
      >
        {trialError ? <div className="form-error">{trialError}</div> : null}
        <p className="modal__hint">
          Full access for 14 days, no card required — no approval needed.
        </p>
        <div className="form-grid">
          <Field label="First name" error={trialFieldErrors.firstName}>
            <input
              value={trial.firstName}
              onChange={(e) => setTrialField("firstName", e.target.value)}
            />
          </Field>
          <Field label="Last name" error={trialFieldErrors.lastName}>
            <input
              value={trial.lastName}
              onChange={(e) => setTrialField("lastName", e.target.value)}
            />
          </Field>
          <Field label="Work email *" error={trialFieldErrors.email}>
            <input
              type="email"
              placeholder="you@company.com"
              value={trial.email}
              onChange={(e) => setTrialField("email", e.target.value)}
              required
            />
          </Field>
          <Field label="Phone number *" error={trialFieldErrors.phone}>
            <PhoneInput
              value={trial.phone}
              onChange={(v) => setTrialField("phone", v)}
              required
            />
          </Field>
          <Field label="Password" error={trialFieldErrors.password} full>
            <span className="login__password">
              <input
                type={showTrialPassword ? "text" : "password"}
                placeholder="At least 6 characters"
                value={trial.password}
                onChange={(e) => setTrialField("password", e.target.value)}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="login__password-toggle"
                onClick={() => setShowTrialPassword((s) => !s)}
                aria-label={showTrialPassword ? "Hide password" : "Show password"}
                title={showTrialPassword ? "Hide password" : "Show password"}
              >
                {showTrialPassword ? (
                  <IconEyeOff size={18} />
                ) : (
                  <IconEye size={18} />
                )}
              </button>
            </span>
          </Field>
          <Field
            label="Organization name"
            error={trialFieldErrors.organizationName}
            full
          >
            <input
              placeholder="Acme Inc."
              value={trial.organizationName}
              onChange={(e) =>
                setTrialField("organizationName", e.target.value)
              }
            />
          </Field>
        </div>
      </Modal>
    </div>
  );
};

export default Landing;
